// src/pages/Waitlist.tsx
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { ArrowRight, AlertTriangle } from "lucide-react";
import { supabase } from "../lib/supabase";
import type { Database } from "../types/supabaseDb";
import Select from "react-select";
import { countryOptions } from "../utils/countries";
import {
  getAllowedCountryEmojis,
  isExcludedCountry,
  excludedCountries,
} from "../utils/countryEmojis";
import ReactConfetti from "react-confetti";
import useScrollToTop from "../hooks/useScrollToTop";
import HyperLayout from "../components/HyperLayout";
import HeroSection from "../components/HeroSection";
import { Link } from "react-router-dom";

type WaitlistInsert = Database["public"]["Tables"]["waitlist"]["Insert"];

// Custom styles for react-select
const selectStyles = {
  control: (base: any) => ({
    ...base,
    borderRadius: "9999px", // Full rounded like other inputs
    padding: "0.25rem 0.75rem",
    border: "1px solid #D1D5DB", // Gray-300
    boxShadow: "none",
    "&:hover": {
      borderColor: "#D1D5DB",
    },
    "&:focus-within": {
      borderColor: "#ff6940", // Primary color
      boxShadow: "0 0 0 2px rgba(255, 105, 64, 0.2)",
    },
  }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#ff6940"
      : state.isFocused
      ? "rgba(255, 105, 64, 0.1)"
      : base.backgroundColor,
    "&:hover": {
      backgroundColor: state.isSelected ? "#ff6940" : "rgba(255, 105, 64, 0.1)",
    },
    color: state.isSelected ? "white" : "black",
  }),
  menu: (base: any) => ({
    ...base,
    zIndex: 50,
  }),
};

export function Waitlist() {
  // Use scroll to top hook
  useScrollToTop();

  const [email, setEmail] = useState("");
  const [country, setCountry] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentGreetingIndex, setCurrentGreetingIndex] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  // Get the allowed country emojis
  const [countryEmojis, setCountryEmojis] = useState("");
  const [selectedCountryExcluded, setSelectedCountryExcluded] = useState(false);

  // Update window dimensions when window resizes
  useEffect(() => {
    const handleResize = () => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const emojis = getAllowedCountryEmojis();
    setCountryEmojis(emojis);
  }, []);

  // Array of greetings in different languages
  const greetings = [
    { text: "hello", language: "English" },
    { text: "你好", language: "Chinese" },
    { text: "안녕하세요", language: "Korean" },
    { text: "こんにちは", language: "Japanese" },
    { text: "hai", language: "Bahasa Melayu" },
    { text: "hola", language: "Español" },
    { text: "bonjour", language: "French" },
  ];

  // Set up the rotation effect
  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentGreetingIndex(
        (prevIndex) => (prevIndex + 1) % greetings.length
      );
    }, 2000);

    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  // Hide confetti after 5 seconds
  useEffect(() => {
    if (showConfetti) {
      const timer = setTimeout(() => {
        setShowConfetti(false);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [showConfetti]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Check if selected country is excluded
      setSelectedCountryExcluded(isExcludedCountry(country));

      // Insert email and country to the waitlist table
      const waitlistData: WaitlistInsert = { email, country };
      const { error: insertError } = await supabase
        .from("waitlist")
        .insert([waitlistData]);

      if (insertError) {
        // Check if the error is about unique violation (already signed up)
        if (
          insertError.code === "23505" ||
          insertError.message.includes("unique") ||
          insertError.message.includes("duplicate")
        ) {
          throw new Error("You are already on the waitlist.");
        }
        throw new Error(insertError.message);
      }

      setIsSuccess(true);
      setShowConfetti(true); // Trigger confetti when form is submitted successfully
      setEmail("");
      setCountry("");
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to join waitlist. Please try again later."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const emojiScrollStyles = {
    container: {
      height: "100%",
      display: "flex",
      flexDirection: "column" as const,
      justifyContent: "center",
      gap: "8px",
    },
    row: {
      display: "inline-block",
      whiteSpace: "nowrap" as const,
    },
    animateLeft: {
      animation: "marquee 20s linear infinite",
    },
    animateRight: {
      animation: "marquee2 20s linear infinite",
      animationDirection: "reverse" as const,
    },
  };

  // Format the excluded countries list for display
  const formattedExcludedCountries = excludedCountries.join(", ");

  return (
    <>
      <Helmet>
        <title>Join the Waitlist | BasedApp</title>
        <meta
          name="description"
          content="Join the BasedApp waitlist to be notified when we launch in your country. We do not provide services to users in certain countries."
        />
        <link rel="canonical" href="https://basedapp.io/waitlist" />
      </Helmet>

      {/* Emoji Confetti */}
      {showConfetti && (
        <ReactConfetti
          width={windowDimensions.width}
          height={windowDimensions.height}
          numberOfPieces={59}
          tweenDuration={5000}
          gravity={0.2}
          initialVelocityY={0}
          run={true}
          recycle={false}
          drawShape={(ctx) => {
            const emojis = ["🍁", "✨"];
            const emoji = emojis[Math.floor(Math.random() * emojis.length)];
            ctx.font = "48px serif";
            ctx.fillText(emoji, 0, 0);
          }}
        />
      )}

      <style>
        {`
          @keyframes marquee {
            0% { transform: translateX(100%); }
            100% { transform: translateX(-100%); }
          }
          @keyframes marquee2 {
            0% { transform: translateX(0%); }
            100% { transform: translateX(-200%); }
          }

          .react-select__control {
            height: 48px;
            background-color: #171717;
            box-shadow: rgb(255, 255, 255) 0px 0px 0px 0px inset, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset, rgba(0, 0, 0, 0) 0px 0px 0px 0px;
            border: none;
            }
            
          .react-select__control:focus-within {
            box-shadow: rgb(255, 255, 255) 0px 0px 0px 0px inset, rgba(255, 107, 66) 0px 0px 0px 2px inset, rgba(0, 0, 0, 0) 0px 0px 0px 0px;
          }

          .react-select__indicator-separator {
            background-color: rgba(255, 255, 255, 0.1)
          }

          .react-select__input-container, .react-select__single-value {
            color: white;
          }

          input::placeholder {
            color: hsl(0, 0%, 50%);
          }
        `}
      </style>

      <HyperLayout>
        <HeroSection className="min-h-screen">
          <div className="pt-16 max-w-xl w-full text-center mx-auto relative z-30">
            <h1 className="text-6xl text-white mb-6 transition-opacity duration-300 font-['Kalam']">
              {greetings[currentGreetingIndex].text}
            </h1>

            {/* Country Emoji Scroll Animation */}
            <div className="h-20 overflow-hidden relative">
              <div style={emojiScrollStyles.container}>
                <div
                  className="text-2xl"
                  style={{
                    ...emojiScrollStyles.row,
                    ...emojiScrollStyles.animateLeft,
                  }}>
                  {countryEmojis}
                </div>
              </div>
            </div>
            <p className="text-lg md:text-xl text-white mb-10 font-sans">
              BasedApp is rolling out to more regions soon. Join the waitlist to
              be notified when we launch in your country.
            </p>
          </div>
          <div className="flex items-center justify-center pb-12 relative z-30">
            {!isSuccess ? (
              <div>
                <div className="max-w-md w-full space-y-8 text-center mx-auto">
                  <form
                    onSubmit={handleSubmit}
                    className="space-y-4 max-w-sm mx-auto">
                    <div className="text-left">
                      <input
                        id="email"
                        type="email"
                        required
                        className="w-full pl-5 pr-4 py-3 ring-1 ring-white/10 rounded-full focus:outline-none focus:ring-2 focus:ring-primary ring-inset bg-card"
                        placeholder="Enter Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>

                    <div className="text-left">
                      <Select
                        id="country"
                        options={countryOptions}
                        styles={selectStyles}
                        placeholder="Select your country"
                        onChange={(option) =>
                          option && setCountry(option.value)
                        }
                        required
                        isSearchable
                        className="react-select-container"
                        classNamePrefix="react-select"
                      />
                    </div>

                    {error && (
                      <div className="text-red-500 bg-red-300/15 p-3 rounded-lg flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        <p className="text-sm">{error}</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isSubmitting || !email || !country}
                      className="w-full flex justify-center items-center px-8 py-3 bg-gradient-to-r from-primary to-secondary text-white rounded-full font-medium hover:opacity-90 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed">
                      {isSubmitting ? (
                        <span className="flex items-center">
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24">
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Processing...
                        </span>
                      ) : (
                        <span className="flex items-center">Join Waitlist</span>
                      )}
                    </button>
                  </form>
                </div>
                <div className="max-w-3xl text-xs text-white/50 mt-12 text-center">
                  <p>Not available anytime soon in these countries:</p>
                  <p className="mt-1">{formattedExcludedCountries}</p>
                </div>
              </div>
            ) : (
              <div className="p-8 space-y-4 max-w-xl w-full ring-1 ring-white/10 rounded-3xl backdrop-blur-sm">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/20 text-emerald-400 mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-8 w-8"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h2 className="text-2xl font-semibold text-white">
                  Thanks for joining!
                </h2>
                <p className="text-white/60">
                  {selectedCountryExcluded
                    ? "Unfortunately we won't be opening up BasedApp to your country so soon - but don't worry. We got you on the waitlist and will keep you updated when it's available."
                    : "We'll notify you when BasedApp is available in your country."}
                </p>
                <div className="pt-4">
                  <Link
                    to="/"
                    className="inline-flex items-center text-primary hover:text-secondary transition-colors">
                    Return to homepage
                    <ArrowRight className="ml-1 w-4 h-4" />
                  </Link>
                </div>
              </div>
            )}
          </div>
        </HeroSection>
      </HyperLayout>
    </>
  );
}

export default Waitlist;
